import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Divider
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

function statusColor(s) {
  if (s === 'open') return 'info';
  if (s === 'resolved' || s === 'closed') return 'success';
  if (s === 'waiting_customer') return 'warning';
  return 'primary';
}

export default function TenantSupportTicketDetailPage() {
  const { ticketId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/support-tickets/${ticketId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setDetail(json);
    } catch (e) {
      setError(e.message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const postReply = async (e) => {
    e.preventDefault();
    const text = reply.trim();
    if (!text) return;
    setPosting(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/support-tickets/${ticketId}/messages`, {
        method: 'POST',
        body: { body: text }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to post');
      setDetail(json);
      setReply('');
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !detail) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!detail?.ticket) return null;

  const t = detail.ticket;
  const messages = detail.messages || [];

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">{t.subject}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {t.ticket_number || `Ticket #${t.id}`}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={t.status.replace(/_/g, ' ')} color={statusColor(t.status)} />
            <Chip size="small" label={t.priority} variant="outlined" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TenantCan permission="tenant.support_tickets.manage">
            <Button variant="outlined" component={Link} to={`/tenant/support/${ticketId}/edit`}>
              Edit
            </Button>
          </TenantCan>
          <Button variant="outlined" component={Link} to="/tenant/support">
            Back to list
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('Category', t.category)}
          {row('Requester', t.requester_email)}
          {row('Assignee', t.assignee_email)}
          {row('Opened', t.opened_at ? String(t.opened_at) : '—')}
          {row('Resolved', t.resolved_at ? String(t.resolved_at) : '—')}
          {row('Closed', t.closed_at ? String(t.closed_at) : '—')}
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {t.description || '—'}
        </Typography>
        {t.resolution_notes ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Resolution notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {t.resolution_notes}
            </Typography>
          </>
        ) : null}
      </Paper>

      <Typography variant="h6" gutterBottom>
        Conversation
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        {messages.length === 0 && <Typography color="text.secondary">No messages yet.</Typography>}
        {messages.map((m) => (
          <Box key={m.id} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {m.created_at ? String(m.created_at) : ''} · {m.author_type.replace(/_/g, ' ')}
              {m.author_email ? ` · ${m.author_email}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {m.body}
            </Typography>
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}

        <TenantCan anyOf={['tenant.support_tickets.view', 'tenant.support_tickets.manage', 'tenant.support_tickets.create']}>
          <Box component="form" onSubmit={postReply} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Add reply"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Visible to your team and platform support"
            />
            <Button type="submit" variant="contained" sx={{ mt: 1 }} disabled={posting || !reply.trim()}>
              {posting ? 'Sending…' : 'Post reply'}
            </Button>
          </Box>
        </TenantCan>
      </Paper>
    </Box>
  );
}
